import { BottomCallout } from "@/components/BottomCallout";
import { Button } from "@/components/Button";
import { ProgressBar } from "@/components/ProgressBar";
import { TextInput } from "@/components/TextInput";
import { TopCallout } from "@/components/TopCallout";
import { faFileAlt, faLightbulb } from "@fortawesome/free-solid-svg-icons";
import { useState, useCallback, useRef, useEffect } from "react";
import { UploadView } from "../components/ManualUpload";
import { RegistrationFormData } from "../context/registration-context";
import { RadioButton } from "@/components/RadioButton";


interface DiagnosticFormProps {
  onContinue: (isSuccess: boolean) => void;
  onBack?: () => void;
  formData: RegistrationFormData;
  updateFormData: (data: Partial<RegistrationFormData>) => void;
}

export const DiagnosticForm: React.FC<DiagnosticFormProps> = ({
  onContinue,
  onBack,
  formData,
  updateFormData
}) => {
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Local state for UI interactions
  const [showDiagnosticProviderForm, setShowDiagnosticProviderForm] = useState(false);
  const [showLetterProviderForm, setShowLetterProviderForm] = useState(false);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
    };
  }, []);

  const handleDiagnosticEvaluationFileChange = (file: File | null) => {
    updateFormData({ diagnosticEvaluationFile: file });
  };

  const handleLetterOfMedicalNecessityFileChange = (file: File | null) => {
    updateFormData({ letterOfMedicalNecessityFile: file });
  };

  const handleProviderInfoChange = (field: string, value: string, type: "diagnostic" | "letter") => {
    const key = type === "diagnostic" ? "diagnosticProviderInfo" : "letterProviderInfo";
    updateFormData({
      [key]: {
        ...formData[key],
        [field]: value
      }
    });
  };

  const isFormValid = () => {
    if (formData.hasAutismDiagnosis === undefined) return false;
    
    if (formData.hasAutismDiagnosis === false) return true; // "No" is valid
    
    if (formData.hasAutismDiagnosis === true) {
      // Check if we have either diagnostic evaluation or letter of medical necessity
      const hasDiagnosticEvaluation = formData.diagnosticEvaluationFile || formData.cdeUrl || 
        (showDiagnosticProviderForm && 
         formData.diagnosticProviderInfo?.providerGroup && 
         formData.diagnosticProviderInfo?.providerName && 
         formData.diagnosticProviderInfo?.providerPhone);
         
      const hasLetterOfMedicalNecessity = formData.letterOfMedicalNecessityFile || formData.lmnUrl || 
        (showLetterProviderForm && 
         formData.letterProviderInfo?.providerGroup && 
         formData.letterProviderInfo?.providerName && 
         formData.letterProviderInfo?.providerPhone);
         
      return hasDiagnosticEvaluation || hasLetterOfMedicalNecessity;
    }
    
    return false;
  };

  const handleSubmit = useCallback(async () => {
    // Prevent multiple submissions
    if (isSubmitting || saving) {
      console.log('⛔ Form already submitting, ignoring click');
      return;
    }

    if (!isFormValid()) {
      setErrors({ general: "Please complete all required fields" });
      return;
    }

    try {
      console.log('🚀 Starting form submission');
      setIsSubmitting(true);
      setSaving(true);
      setErrors({});

      // Clear any existing timeout
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }

      console.log('✅ Form submitted successfully');
      // Call onContinue with success status - the parent component will handle the API call
      // Don't reset states here - let the parent component handle navigation
      onContinue(true);
      
    } catch (error: any) {
      console.error('❌ Error in diagnostic form:', error);
      setErrors({ general: "Something went wrong. Please try again." });
    } finally {
      // Always reset states regardless of success or failure
      setIsSubmitting(false);
      setSaving(false);
    }
  }, [isSubmitting, saving, isFormValid, onContinue]);

  return (
    <>
      <TopCallout icon={faFileAlt} title="Insurance Coverage for ABA">
        If your child has been diagnosed, we can help you move forward fast and with less stress.
      </TopCallout>

      <div className="bg-white p-8 rounded-[16px] shadow-lg">
        <ProgressBar currentStep={4} totalSteps={5} />


        {/* Main Question */}
        <div className="mb-8">
          <RadioButton
            question="Has the individual looking to receive services been diagnosed with Autism Spectrum Disorder?"
            name="autism-diagnosis"
            defaultValue={
              formData.hasAutismDiagnosis === true
                ? "yes"
                : formData.hasAutismDiagnosis === false
                ? "no"
                : undefined
            }
            onValueChange={(value) => {
              const boolValue = value === "yes";
              updateFormData({ hasAutismDiagnosis: boolValue });
            }}
            options={[
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ]}
          />
        </div>

        {/* Diagnostic Evaluation Section - Only show if "Yes" selected */}
        {formData.hasAutismDiagnosis === true && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4">
              Please upload a copy of the full diagnosis completed by a medical
              professional. The diagnosis should include:
            </h3>
            <ul className="list-disc list-inside mb-4 text-sm text-gray-700 space-y-1">
              <li>Diagnostic tools used (e.g. ADOS, ADI-R)</li>
              <li>Formal autism diagnosis code (F84.0)</li>
              <li>A recommendation for ABA therapy</li>
            </ul>

            <div className="space-y-4">
              {!showDiagnosticProviderForm && (
                <div>
                  <UploadView
                    text="Upload / Drag & Drop File"
                    onChange={handleDiagnosticEvaluationFileChange}
                    value={formData.diagnosticEvaluationFile || formData.cdeUrl}
                    onRemove={() => handleDiagnosticEvaluationFileChange(null)}
                    allowPdf={true}
                  />
                </div>
              )}

              <div className="flex justify-center">
                <Button
                  variant="link"
                  className="!text-primary !font-medium !text-base"
                  onClick={() =>
                    setShowDiagnosticProviderForm(!showDiagnosticProviderForm)
                  }
                  type="button"
                >
                  {showDiagnosticProviderForm
                    ? "Upload document instead"
                    : "I don't have this document"}
                </Button>
              </div>

              {showDiagnosticProviderForm && (
                <div className="bg-secondary p-4 rounded-lg mb-4">
                  <p className="text-sm text-black font-bold text-center mb-4">
                    Insurance requires a diagnosis to receive ABA therapy. Not
                    to worry, Headstart will reach out to your diagnosing
                    provider to obtain the necessary information!
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Diagnosing Provider Group / Facility (e.g. Pediatric
                        Associates)
                      </label>
                      <TextInput
                        value={
                          formData.diagnosticProviderInfo?.providerGroup || ""
                        }
                        onChange={(e) =>
                          handleProviderInfoChange(
                            "providerGroup",
                            e.target.value,
                            "diagnostic"
                          )
                        }
                        placeholder="Enter provider group or facility name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Diagnosing Provider (e.g. Dr. James Harrison)
                      </label>
                      <TextInput
                        value={
                          formData.diagnosticProviderInfo?.providerName || ""
                        }
                        onChange={(e) =>
                          handleProviderInfoChange(
                            "providerName",
                            e.target.value,
                            "diagnostic"
                          )
                        }
                        placeholder="Enter provider's full name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Diagnosing Provider Phone Number
                      </label>
                      <TextInput
                        type="tel"
                        value={
                          formData.diagnosticProviderInfo?.providerPhone || ""
                        }
                        onChange={(e) =>
                          handleProviderInfoChange(
                            "providerPhone",
                            e.target.value,
                            "diagnostic"
                          )
                        }
                        placeholder="Enter provider's phone number"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Letter of Medical Necessity Section - Only show if "Yes" selected */}
        {formData.hasAutismDiagnosis === true && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4">
              Please upload a copy of a letter of medical necessity for ABA
              therapy from your diagnosing provider.
            </h3>

            <div className="space-y-4">
              {!showLetterProviderForm && (
                <div>
                  <UploadView
                    text="Upload / Drag & Drop File"
                    onChange={handleLetterOfMedicalNecessityFileChange}
                    value={
                      formData.letterOfMedicalNecessityFile || formData.lmnUrl
                    }
                    onRemove={() =>
                      handleLetterOfMedicalNecessityFileChange(null)
                    }
                    allowPdf={true}
                  />
                </div>
              )}

              <div className="flex justify-center">
                <Button
                  variant="link"
                  className="!text-primary !font-medium !text-base"
                  onClick={() =>
                    setShowLetterProviderForm(!showLetterProviderForm)
                  }
                  type="button"
                >
                  {showLetterProviderForm
                    ? "Upload document instead"
                    : "I don't have this document"}
                </Button>
              </div>

              {showLetterProviderForm && (
                <div className="bg-secondary p-4 rounded-lg mb-4">
                  <p className="text-sm text-black font-bold text-center mb-4">
                    Not to worry, Headstart will reach out to your diagnosing
                    provider to obtain the necessary information!
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Diagnosing Provider Group / Facility (e.g. Pediatric
                        Associates)
                      </label>
                      <TextInput
                        value={
                          formData.letterProviderInfo?.providerGroup ||
                          formData.diagnosticProviderInfo?.providerGroup ||
                          ""
                        }
                        onChange={(e) =>
                          handleProviderInfoChange(
                            "providerGroup",
                            e.target.value,
                            "letter"
                          )
                        }
                        placeholder="Enter provider group or facility name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Diagnosing Provider (e.g. Dr. James Harrison)
                      </label>
                      <TextInput
                        value={
                          formData.letterProviderInfo?.providerName ||
                          formData.diagnosticProviderInfo?.providerName ||
                          ""
                        }
                        onChange={(e) =>
                          handleProviderInfoChange(
                            "providerName",
                            e.target.value,
                            "letter"
                          )
                        }
                        placeholder="Enter provider's full name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Diagnosing Provider Phone Number
                      </label>
                      <TextInput
                        type="tel"
                        value={
                          formData.letterProviderInfo?.providerPhone ||
                          formData.diagnosticProviderInfo?.providerPhone ||
                          ""
                        }
                        onChange={(e) =>
                          handleProviderInfoChange(
                            "providerPhone",
                            e.target.value,
                            "letter"
                          )
                        }
                        placeholder="Enter provider's phone number"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error Display */}
        {errors.general && (
          <div className="text-red-500 text-sm mb-4">{errors.general}</div>
        )}

        {/* Navigation Buttons */}
        <div className="flex gap-3">
          {onBack && (
            <Button
              type="button"
              variant="gray"
              className="!rounded-full flex-1"
              onClick={onBack}
              disabled={saving}
            >
              Back
            </Button>
          )}

          <Button
            type="button"
            variant="primary"
            className="!rounded-full flex-1"
            onClick={handleSubmit}
            disabled={!isFormValid() || saving || isSubmitting}
            loading={saving || isSubmitting}
          >
            Continue
          </Button>
        </div>
      </div>

      <BottomCallout icon={faLightbulb}>
        You're not alone. Thousands of families have stood right where you are
        and found support that works with ABA therapy.
      </BottomCallout>
    </>
  );
};
